import React, { useState, useEffect, useRef, useMemo } from "react";
import styles from "./Invoice.module.css";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import moment from "moment";
import { useHistory } from "react-router-dom";
import { toCommas } from "../../utils/utils";
import { useSnackbar } from 'react-simple-snackbar';
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineRoundedIcon from "@material-ui/icons/DeleteOutlineRounded";
import DateFnsUtils from "@date-io/date-fns";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import InputBase from "@material-ui/core/InputBase";
import { Container, Grid } from "@material-ui/core";
import Divider from "@material-ui/core/Divider";
import SaveIcon from "@material-ui/icons/Save";
import Button from "@material-ui/core/Button";
import { initialState } from "../../initialState";
import currencies from "../../currencies.json";
import {
  createInvoice,
  getInvoice,
  updateInvoice,
} from "../../actions/invoiceActions";
import { formatDollarsToCents, formatCentsToDollars, dollarFormatter } from "../../utils/money";
import { getAccountsByUser } from "../../actions/accountActions";
import { getLocationsByUser } from "../../actions/locationActions";
import { getContactsByUser } from "../../actions/contactActions";
import { getProductsByUser } from "../../actions/productActions";
import { getLockupsByUser, updateLockup } from "../../actions/lockupActions";
import { getInventoriesByUser, updateInventory } from "../../actions/inventoryActions";
import { fetchMarginComparison, fetchPcModelInventory, fetchTaxRate, updateQuote } from "../../api/index";
import InvoiceType from "./InvoiceType";
import MarginComparisonDialog from "./MarginComparisonDialog";
import { calculateMarginComparison, getRecommendedDeliveryTierId } from "../../utils/marginComparison";
import { HAUL_AWAY_OPTIONS, formatHaulAwayOption, getSavedHaulAwayCost, getSavedHaulAwayFee } from "../../utils/haulAwayPricing";
import { useLocation } from "react-router-dom";
import {Checkbox, FormGroup, FormControlLabel} from "@material-ui/core";
import AddLocation from "../Locations/AddLocation";
import AddContact from "../Contacts/AddContact";
import AddProduct from "../Products/AddProduct";
import useMediaQuery from "@material-ui/core/useMediaQuery";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    "& > *": {
      margin: theme.spacing(1),
    },
  },
  large: {
    width: theme.spacing(12),
    height: theme.spacing(12),
  },
  table: {
    minWidth: 650,
  },

  headerContainer: {
    // display: 'flex'
    paddingTop: theme.spacing(1),
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(1),
  },
}));

const createInvoiceRequestId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const PC_PRIORITY_KEYS = [
  'model',
  'modelNumber',
  'description',
  'desc',
  'quantity',
  'qty',
  'available',
  'onHand',
  'store',
  'location',
  'price',
  'cost',
  'status',
]

const formatPcKey = (key = '') =>
  String(key)
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())

const formatPcValue = (value) => {
  if (value === null || value === undefined || value === '') return 'N/A'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

const getPcInventoryRows = (payload) => {
  if (!payload) return []
  if (Array.isArray(payload)) return payload
  if (typeof payload !== 'object') return [{ result: payload }]

  const knownArrayKeys = ['data', 'results', 'items', 'rows', 'inventory', 'records']
  for (const key of knownArrayKeys) {
    if (Array.isArray(payload[key])) return payload[key]
  }

  const nestedArray = Object.values(payload).find((value) => Array.isArray(value))
  if (nestedArray) return nestedArray
  return [payload]
}

const getPcDisplayPairs = (row = {}) => {
  if (!row || typeof row !== 'object' || Array.isArray(row)) {
    return [['Result', formatPcValue(row)]]
  }

  const entries = Object.entries(row).filter(([, value]) => value !== null && value !== undefined && value !== '')
  const normalizedEntries = entries.map(([key, value]) => ({ key, value, normalizedKey: key.toLowerCase().replace(/[^a-z0-9]/g, '') }))
  const priorityPairs = PC_PRIORITY_KEYS
    .map((priorityKey) => normalizedEntries.find((entry) => entry.normalizedKey === priorityKey.toLowerCase().replace(/[^a-z0-9]/g, '')))
    .filter(Boolean)

  const selected = priorityPairs.length ? priorityPairs : normalizedEntries.slice(0, 6)
  return selected.slice(0, 6).map(({ key, value }) => [formatPcKey(key), formatPcValue(value)])
}

const Invoice = () => {
  const location = useLocation();
  const [invoiceData, setInvoiceData] = useState(initialState);
  const [product, setProduct] = useState(null)
  const [lockup, setLockup] = useState(null)
  const [dno, setDno] = useState(null);
  const [rates, setRates] = useState(0.00);
  const [deliveryFee, setDeliveryFee] = useState(0.00);
  const [deliveryCost, setDeliveryCost] = useState(0.00);
  const [deliveryFeeOverride, setDeliveryFeeOverride] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingMargins, setIsCheckingMargins] = useState(false);
  const [marginComparison, setMarginComparison] = useState(null);
  const [marginDialogOpen, setMarginDialogOpen] = useState(false);
  const [salesTax, setSalesTax] = useState(0);
  const [subTotal, setSubTotal] = useState(0);
  const [total, setTotal] = useState(0);
  const [selectedRbDate, setSelectedRbDate] = useState(
    moment().add(1, 'days')
  );
  const [selectedDate, setSelectedDate] = useState(
    moment(selectedRbDate).add(1, 'days')
  );
  const [haulAway, setHaulAway] = useState(false);
  const [haulAwayFee, setHaulAwayFee] = useState(0);
  const [haulAwayCost, setHaulAwayCost] = useState(0);
  const [walkUpCharges, setWalkUpCharges] = useState([{ walkUp: '', quantity: '1' }]);
  const [haulAwayQuantity, setHaulAwayQuantity] = useState('1');
  const [account, setAccount] = useState(null);
  const [locationTarget, setLocationTarget] = useState(null);
  const [contactTarget, setContactTarget] = useState(null);
  const [nextInvoiceNumber, setNextInvoiceNumber] = useState('');
  const [openSnackbar, closeSnackbar] = useSnackbar()
  const [type, setType] = useState("Invoice");
  const [status, setStatus] = useState("");
  const { id } = useParams();
  const isEditingInvoice = Boolean(id);
  const accounts = useSelector((state) => state.accounts.accounts || []);
  const locations = useSelector((state) => state.locations.locations || []);
  const contacts = useSelector((state) => state.contacts.contacts || []);
  const products = useSelector((state) => state.products.products);
  const lockups = useSelector((state) => state.lockups.lockups);
  const inventories = useSelector((state) => state.inventories.inventories)
  const { invoice } = useSelector((state) => state.invoices);
  const activeInvoice = isEditingInvoice ? invoice : null;
  const dispatch = useDispatch();
  const history = useHistory();
  const user = JSON.parse(localStorage.getItem("profile"));
  const originalItems = useRef(activeInvoice ? activeInvoice.items : [
      {modelNumber: '', unitPrice: '', quantity: '', discount: '', unitCost: '', doNotOrder: false, lockup: false},
  ]);
  const createRequestIdRef = useRef(createInvoiceRequestId());
  const quoteDraftAppliedRef = useRef(false);
  const quoteDraftQuoteIdRef = useRef(null);
  console.log(originalItems)
  const originalQuantity = useRef('');
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [pcLookupByModel, setPcLookupByModel] = useState({});
  const [pcLookupLoadingByModel, setPcLookupLoadingByModel] = useState({});
  useEffect(() => {
    if (locationTarget) {
      applyTaxRateFromLocation(locationTarget)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationTarget])

  useEffect(() => {
    if (id){
      dispatch(getInvoice(id));
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const search = user?.result._id || user?.result?.googleId
    dispatch(
      getAccountsByUser({ search })
    );
    dispatch(
      getLocationsByUser({ search })
    );
    dispatch(
      getContactsByUser({ search })
    );
    dispatch(
      getProductsByUser({ search: user?.result._id || user?.result?.googleId })
    );
    dispatch(
      getLockupsByUser({ search: user?.result._id || user?.result?.googleId })
    );
    dispatch(
      getInventoriesByUser({ search: user?.result._id || user?.result?.googleId })
    );
    if (!id) {
      fetch(`${process.env.REACT_APP_API}/invoices/next-number`, { credentials: 'include' })
        .then(res => res.ok ? res.json() : Promise.reject(res.status))
        .then(data => setNextInvoiceNumber(data?.invoiceNumber || ''))
        .catch(() => setNextInvoiceNumber(''))
    }
    // eslint-disable-next-line
  }, [dispatch]);

  useEffect(() => {
    if (isEditingInvoice || quoteDraftAppliedRef.current) return
    const quoteDraft = location.state?.quoteDraft
    if (!quoteDraft) return

    quoteDraftAppliedRef.current = true
    const draftItems = Array.isArray(quoteDraft.items) && quoteDraft.items.length
      ? quoteDraft.items
      : initialState.items
    const draftType = quoteDraft.type || 'Quotation'
    const draftStatus = quoteDraft.status || 'Unpaid'
    const draftShipTo = quoteDraft.shipTo || null
    const draftDeliveryFee = Number(quoteDraft.deliveryFee) || 0
    const draftDeliveryFeeOverride = Boolean(quoteDraft.deliveryFeeOverride || draftDeliveryFee > 0)
    quoteDraftQuoteIdRef.current = quoteDraft.quoteId || null

    setInvoiceData((prev) => ({
      ...prev,
      type: draftType,
      status: draftStatus,
      items: draftItems,
      shipTo: draftShipTo || prev.shipTo,
      haulAway: Boolean(quoteDraft.haulAway),
      haulAwayFee: getSavedHaulAwayFee(quoteDraft),
      haulAwayCost: getSavedHaulAwayCost(quoteDraft),
      haulAwayQuantity: getSavedServiceQuantity(quoteDraft.haulAwayQuantity),
      walkUp: quoteDraft.walkUp || prev.walkUp,
      walkUpCharges: Array.isArray(quoteDraft.walkUpCharges) ? quoteDraft.walkUpCharges : prev.walkUpCharges,
      walkUpQuantity: quoteDraft.walkUpQuantity || prev.walkUpQuantity,
      walkupHaulawayUnits: quoteDraft.walkupHaulawayUnits || prev.walkupHaulawayUnits,
      walkUpFee: quoteDraft.walkUpFee ?? prev.walkUpFee,
      deliveryFee: quoteDraft.deliveryFee ?? prev.deliveryFee,
      deliveryFeeOverride: draftDeliveryFeeOverride,
    }))
    setType(draftType)
    setStatus(draftStatus)
    setHaulAway(Boolean(quoteDraft.haulAway))
    setHaulAwayFee(getSavedHaulAwayFee(quoteDraft))
    setHaulAwayCost(getSavedHaulAwayCost(quoteDraft))
    setHaulAwayQuantity(String(getSavedServiceQuantity(quoteDraft.haulAwayQuantity)))
    setWalkUpCharges(normalizeWalkUpChargeRowsForState(quoteDraft.walkUpCharges, quoteDraft.walkUp, quoteDraft.walkUpQuantity, quoteDraft.walkUpFee))
    if (quoteDraft.deliveryFee !== undefined && quoteDraft.deliveryFee !== null) setDeliveryFee(draftDeliveryFee)
    setDeliveryFeeOverride(draftDeliveryFeeOverride)
    if (draftShipTo) setLocationTarget(draftShipTo)
    if (quoteDraft.receivedByDate) {
      const receivedByDate = moment(quoteDraft.receivedByDate)
      if (receivedByDate.isValid()) {
        setSelectedRbDate(receivedByDate)
        setSelectedDate(moment(receivedByDate).add(1, 'days'))
      }
    }
  }, [isEditingInvoice, location.state])

  useEffect(() => {
    if (activeInvoice) {
      //Automatically set the default invoice values as the ones in the invoice to be updated
      setInvoiceData(activeInvoice);
      setRates(activeInvoice.rates);
      const savedHaulAwayFee = getSavedHaulAwayFee(activeInvoice);
      setHaulAway(savedHaulAwayFee > 0);
      setHaulAwayFee(savedHaulAwayFee);
      setHaulAwayCost(getSavedHaulAwayCost(activeInvoice));
      setWalkUpCharges(normalizeWalkUpChargeRowsForState(activeInvoice.walkUpCharges, activeInvoice.walkUp, activeInvoice.walkUpQuantity, activeInvoice.walkUpFee));
      setHaulAwayQuantity(String(getSavedServiceQuantity(activeInvoice.haulAwayQuantity, activeInvoice.walkupHaulawayUnits)));
      setAccount(activeInvoice.billTo || null);
      setLocationTarget(activeInvoice.shipTo || null);
      setContactTarget(getPrimaryInvoiceContact(activeInvoice));
      setType(activeInvoice.type);
      setStatus(activeInvoice.status);
      setSelectedDate(activeInvoice.dueDate);
      setSelectedRbDate(activeInvoice.receivedByDate);
      setDeliveryFee(typeof activeInvoice.deliveryFee === 'number' ? activeInvoice.deliveryFee : 0);
      setDeliveryCost(typeof activeInvoice.deliveryCost === 'number' ? activeInvoice.deliveryCost : 0);
      setDeliveryFeeOverride(Boolean(activeInvoice.deliveryFeeOverride));
    }
  }, [activeInvoice]);

  useEffect(() => {
    if (!activeInvoice) return
    const primaryInvoiceContact = getPrimaryInvoiceContact(activeInvoice)
    if (activeInvoice?.billTo?.accountId && (!account || getAccountId(account) !== activeInvoice.billTo.accountId)) {
      const acc = accounts.find((a) => a._id === activeInvoice.billTo.accountId)
      if (acc) setAccount(acc)
    }
    const invoiceShipLocationId = getLocationId(activeInvoice?.shipTo)
    const shouldHydrateShipTo = invoiceData?.shipTo !== null
    if (shouldHydrateShipTo) {
      if (invoiceShipLocationId && (!locationTarget || getLocationId(locationTarget) !== invoiceShipLocationId)) {
        const loc = locations.find((l) => String(l._id) === String(invoiceShipLocationId))
        if (loc) setLocationTarget(loc)
      } else if (!invoiceShipLocationId && activeInvoice?.shipTo && (!locationTarget || !getLocationId(locationTarget))) {
        const loc = locations.find((l) => shipToMatchesLocation(activeInvoice.shipTo, l))
        if (loc) setLocationTarget(loc)
      }
    }
    if (primaryInvoiceContact?.contactId && (!contactTarget || getContactId(contactTarget) !== primaryInvoiceContact.contactId)) {
      const con = contacts.find((c) => c._id === primaryInvoiceContact.contactId)
      if (con) setContactTarget(con)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts, locations, contacts, activeInvoice, account, locationTarget, contactTarget, invoiceData?.shipTo])

  useEffect(() => {
    if (type === "Receipt") {
      setStatus("Paid");
    } else {
      setStatus("Unpaid");
    }
  }, [type]);

const WALK_UP_FEES = {
  '3rd floor': 8,
  '4th floor': 12,
  '5th floor': 18,
  '6th floor': 22,
}

const WALK_UP_QUANTITY_LIMIT_MESSAGE = 'Cannot have more walk ups than total items.'
const HAUL_AWAY_QUANTITY_LIMIT_MESSAGE = 'Cannot have more haul aways than total items.'

const isStandardWalkUp = (walkUp) => Object.prototype.hasOwnProperty.call(WALK_UP_FEES, walkUp)

const getWalkUpFee = (walkUp) => WALK_UP_FEES[walkUp] || 0

const normalizeWalkupHaulawayUnits = (value) => {
  const units = Number(value);
  return Number.isFinite(units) && units > 0 ? units : 1;
}

const getSavedServiceQuantity = (value, legacyValue = 1) => {
  if (value !== undefined && value !== null && value !== '') return normalizeWalkupHaulawayUnits(value);
  return normalizeWalkupHaulawayUnits(legacyValue);
}

const getLiveServiceQuantity = (value) => {
  if (value === '') return 0;
  const units = Number(value);
  return Number.isFinite(units) && units > 0 ? units : 0;
}

const createWalkUpChargeRow = (row = {}) => ({
  walkUp: row.walkUp || row.label || '',
  quantity: row.quantity !== undefined && row.quantity !== null ? String(row.quantity) : '1',
  rate: row.rate !== undefined && row.rate !== null ? Number(row.rate) || 0 : undefined,
  total: row.total !== undefined && row.total !== null ? Number(row.total) || 0 : undefined,
})

const normalizeWalkUpChargeRowsForState = (charges = [], fallbackWalkUp = '', fallbackQuantity = 1, fallbackTotal = 0) => {
  const rows = Array.isArray(charges)
    ? charges.map(createWalkUpChargeRow).filter((row) => row.walkUp)
    : []
  if (rows.length) return rows
  if (fallbackWalkUp) {
    return [createWalkUpChargeRow({
      walkUp: fallbackWalkUp,
      quantity: getSavedServiceQuantity(fallbackQuantity, 1),
      total: Number(fallbackTotal) || 0,
    })]
  }
  return [createWalkUpChargeRow()]
}

const getActiveWalkUpChargeRows = (rows = [], quantityNormalizer = getLiveServiceQuantity) => (
  (Array.isArray(rows) ? rows : [])
    .filter((row) => row?.walkUp)
    .map((row) => {
      const quantity = quantityNormalizer(row.quantity)
      const rate = isStandardWalkUp(row.walkUp) ? getWalkUpFee(row.walkUp) : Number(row.rate) || 0
      const total = isStandardWalkUp(row.walkUp) ? rate * quantity : Number(row.total) || 0
      return {
        walkUp: row.walkUp,
        label: row.walkUp,
        quantity,
        rate,
        total,
      }
    })
    .filter((row) => row.quantity > 0 && row.total > 0)
)

const getWalkUpLegacyFields = (rows = []) => {
  const activeRows = getActiveWalkUpChargeRows(rows, normalizeWalkupHaulawayUnits)
  const quantity = activeRows.reduce((sum, row) => sum + (Number(row.quantity) || 0), 0)
  const totalFee = activeRows.reduce((sum, row) => sum + (Number(row.total) || 0), 0)
  const walkUpLabel = activeRows.length === 1
    ? activeRows[0].walkUp
    : activeRows.length > 1
      ? 'Multiple walk-ups'
      : ''
  return {
    walkUp: walkUpLabel,
    walkUpCharges: activeRows,
    walkUpQuantity: quantity || 1,
    walkUpFee: totalFee,
  }
}

const getWalkUpQuantityTotal = (rows = [], quantityNormalizer = getLiveServiceQuantity) => (
  (Array.isArray(rows) ? rows : [])
    .filter((row) => row?.walkUp)
    .reduce((sum, row) => sum + quantityNormalizer(row.quantity), 0)
)

const capWalkUpChargeRowsToQuantity = (rows = [], maxQuantity = 0) => {
  let remaining = Math.max(Number(maxQuantity) || 0, 0)
  return (Array.isArray(rows) ? rows : []).map((row) => {
    if (!row?.walkUp) return row
    if (remaining <= 0) return createWalkUpChargeRow()
    const rowQuantity = getLiveServiceQuantity(row.quantity)
    const cappedQuantity = Math.min(rowQuantity || 1, remaining)
    remaining -= cappedQuantity
    return { ...row, quantity: String(cappedQuantity) }
  })
}

  const defaultProps = {
    options: currencies,
    getOptionLabel: (option) => option.label,
  };

  const toArray = (value) => {
    if (!value) return []
    return Array.isArray(value) ? value.filter(Boolean) : [value]
  }

  const normalizeInvoiceContactList = (inv) => [
    ...toArray(inv?.contactList),
    ...toArray(inv?.contact),
  ]

  const getPrimaryInvoiceContact = (inv) => normalizeInvoiceContactList(inv)[0] || null

  const getAccountId = (acc) => acc?._id || acc?.accountId || null
  const getLocationId = (loc) => loc?._id || loc?.locationId || null
  const getContactId = (con) => con?._id || con?.contactId || null
  const matchesText = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase()
  const shipToMatchesLocation = (shipToSnapshot, loc) => {
    if (!shipToSnapshot || !loc) return false
    const shipAccountId = String(shipToSnapshot.accountId || '')
    const locAccountId = String(loc.accountId || '')
    if (shipAccountId && locAccountId && shipAccountId !== locAccountId) return false
    const nameMatch = matchesText(shipToSnapshot.name, loc.name)
    const addressMatch = matchesText(shipToSnapshot.shippingAddress, loc.shippingAddress)
    const zipMatch = matchesText(shipToSnapshot.shippingZip, loc.shippingZip)
    const cityMatch = matchesText(shipToSnapshot.shippingCity, loc.shippingCity)
    const stateMatch = matchesText(shipToSnapshot.shippingState, loc.shippingState)
    return nameMatch && (addressMatch || (zipMatch && cityMatch && stateMatch))
  }

  const selectedAccountId = getAccountId(account)
  const selectedLocationId = getLocationId(locationTarget)

  const accountOptions = useMemo(() => accounts, [accounts])
  const locationOptions = useMemo(() => {
    if (!account) return locations
    return locations.filter((l) => String(l.accountId) === String(selectedAccountId))
  }, [locations, account, selectedAccountId])
  const contactMatchesLocation = (contact, locationId) => {
    if (!contact || !locationId) return false
    const ids = Array.isArray(contact.locationIds) && contact.locationIds.length
      ? contact.locationIds
      : (contact.locationId ? [contact.locationId] : [])
    return ids.some((id) => String(id) === String(locationId))
  }
  const locationContactsForTarget = useMemo(() => {
    if (!locationTarget) return []
    const resolvedLocation = getLocationId(locationTarget)
      ? locationTarget
      : locations.find((l) => shipToMatchesLocation(locationTarget, l))
    const targetLocationId = getLocationId(resolvedLocation)
    const invoiceShipToId = getLocationId(activeInvoice?.shipTo)
      ? String(getLocationId(activeInvoice.shipTo))
      : (resolvedLocation && shipToMatchesLocation(activeInvoice?.shipTo, resolvedLocation) ? String(getLocationId(resolvedLocation)) : null)
    const isOriginalInvoiceLocation = invoiceShipToId && String(targetLocationId) === invoiceShipToId
    const locContacts = Array.isArray(locationTarget.contacts) ? locationTarget.contacts : []
    if (locContacts.length) return locContacts
    const matchingContacts = contacts.filter((c) => contactMatchesLocation(c, targetLocationId))
    if (matchingContacts.length) return matchingContacts
    const invoiceContacts = normalizeInvoiceContactList(activeInvoice)
    const invoiceMatchingContacts = invoiceContacts.filter((c) => contactMatchesLocation(c, targetLocationId))
    if (invoiceMatchingContacts.length) return invoiceMatchingContacts
    const primaryInvoiceContact = getPrimaryInvoiceContact(activeInvoice)
    if (isOriginalInvoiceLocation && primaryInvoiceContact) return [primaryInvoiceContact]
    return []
  }, [contacts, locations, locationTarget, activeInvoice])

  const productsProps = {
    options: products,
    getOptionLabel: (option) => option.modelNumber,
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const handleRbDateChange = (date) => {
    setSelectedRbDate(date);
    setPcLookupByModel({});
    let days = account ? account.term : 1;
    let tomorrow = moment(date).add(days, 'days');
    setSelectedDate(tomorrow);
  };

  const handleRates = (e) => {
    setRates(e.target.value);
    setInvoiceData((prevState) => ({ ...prevState, tax: e.target.value }));
  };

  // Auto-assign contacts based on selected location
  useEffect(() => {
    const locContacts = locationContactsForTarget;
    const primaryLocContact = locContacts[0] || null;
    if (primaryLocContact) {
      setContactTarget(primaryLocContact);
    }
    setInvoiceData((prev) => {
      const sameShip = prev.shipTo === (locationTarget || prev.shipTo);
      if (!locContacts.length) {
        if (sameShip) return prev;
        return {
          ...prev,
          shipTo: locationTarget || prev.shipTo,
        };
      }
      return {
        ...prev,
        shipTo: locationTarget || prev.shipTo,
        contact: primaryLocContact,
        contactList: locContacts,
      };
    });
  }, [locationContactsForTarget, locationTarget]);

  const applyTaxRateFromLocation = async (loc) => {
    const state = loc?.shippingState
    const zip = loc?.shippingZip
    if (!state || !zip) return

    try {
      const { data } = await fetchTaxRate({ state, zip })
      const percent = data.estimatedCombinedRate > 1 ? data.estimatedCombinedRate : data.estimatedCombinedRate * 100
      setRates(percent)
      setInvoiceData((prev) => ({ ...prev, rates: percent }))
    } catch (error) {
      console.log('Tax rate lookup failed', error)
    }
  }

  const handleDeliveryFeeChange = (e) => {
    let value = 0
    if (e.target.value && e.target.value !== '') {
      value = parseFloat(e.target.value)
    }
    setDeliveryFee(value)
    
    setInvoiceData((prevState) => ({ ...prevState, deliveryFee: value}));
  }

  const handleDeliveryFeeOverride = (e) => {
    const checked = e.target.checked
    setDeliveryFeeOverride(checked);
    if (!checked) {
      setDeliveryCost(0)
      setInvoiceData((prevState) => ({ ...prevState, deliveryFeeOverride: checked, deliveryCost: 0 }));
      return
    }
    setInvoiceData((prevState) => ({ ...prevState, deliveryFeeOverride: checked}));
  }

  const handleDeliveryCostChange = (e) => {
    let value = 0
    if (e.target.value && e.target.value !== '') {
      value = parseFloat(e.target.value)
    }
    setDeliveryCost(value)
    setInvoiceData((prevState) => ({ ...prevState, deliveryCost: value }))
  }

  const handleDoNotOrderClick = (index, e) => {
    const values = [...invoiceData.items];
    values[index][e.target.name] = e.target.checked;
    if (values[index]['lockup'] === true) {
      values[index][e.target.name] = !e.target.checked;
      e.target.checked = false;
      openSnackbar('Deselect lockup to select inventory.')
    } else {
      if (e.target.checked) {
          let inv = inventories.find(obj => {
            if (obj.modelNumber === values[index]['modelNumber']) {
              if (obj.quantity < values[index]['quantity']) {
                openSnackbar(`There are only ${obj.quantity} ${obj.modelNumber} in inventory.`)
                values[index][e.target.name] = !e.target.checked;
                e.target.checked = false;
              } else {
                values[index]['unitPrice'] = formatCentsToDollars(obj.price);
                values[index]['unitCost'] = formatCentsToDollars(obj.cost);
                setDno(obj);
              }
              return true;
            } else {
              return false;
            }
          })
          if (inv === undefined) {
              openSnackbar(`${values[index]['modelNumber']} is not in inventory.`)
              values[index][e.target.name] = !e.target.checked;
              e.target.checked = false;
          }
      } else {
        products.find(obj => {
          if (obj.modelNumber === values[index]['modelNumber']) {
            values[index]['unitPrice'] = formatCentsToDollars(obj.price);
            values[index]['unitCost'] = formatCentsToDollars(obj.cost);
            return true;
          } else {
            return false;
          }
        })
      }
    }
    setInvoiceData({...invoiceData, items: values})
  }

  const handleLockupClick = (index, e) => {
    const values = [...invoiceData.items];
    values[index][e.target.name] = e.target.checked;
    if (values[index]['doNotOrder'] === true) {
      values[index][e.target.name] = !e.target.checked;
      e.target.checked = false;
      openSnackbar('Deselect inventory to select lockup.')
    } else {
      if (e.target.checked) {
        let l = lockups.find(obj => {
          if (obj.modelNumber === values[index]['modelNumber']) {
            if (obj.quantity < values[index]['quantity']) {
              openSnackbar(`There are only ${obj.quantity} ${obj.modelNumber} in lockup.`)
              values[index][e.target.name] = !e.target.checked;
              e.target.checked = false;
            } else {
              values[index]['unitPrice'] = formatCentsToDollars(obj.price);
              values[index]['unitCost'] = formatCentsToDollars(obj.cost);
              setLockup(obj);
            }
            return true;
          } else {
            return false;
          }
        })
        if (l === undefined) {
            openSnackbar(`${values[index]['modelNumber']} is not in lockup.`)
            values[index][e.target.name] = !e.target.checked;
            e.target.checked = false;
        }
    } else {
      products.find(obj => {
        if (obj.modelNumber === values[index]['modelNumber']) {
          values[index]['unitPrice'] = formatCentsToDollars(obj.price);
          values[index]['unitCost'] = formatCentsToDollars(obj.cost);
          return true;
        } else {
          return false;
        }
      })
    }
    }
    setInvoiceData({...invoiceData, items: values})
  }

  const lookupPcModelInventory = async (modelNumber) => {
    const normalizedModel = String(modelNumber || '').trim().toUpperCase()
    if (!normalizedModel || pcLookupLoadingByModel[normalizedModel]) return

    setPcLookupLoadingByModel((prev) => ({ ...prev, [normalizedModel]: true }))
    try {
      const receiveByDate = selectedRbDate ? moment(selectedRbDate).format('YYYY-MM-DD') : ''
      const { data } = await fetchPcModelInventory(normalizedModel, receiveByDate)
      setPcLookupByModel((prev) => ({
        ...prev,
        [normalizedModel]: {
          data,
          error: null,
          checkedAt: new Date().toISOString(),
        },
      }))
    } catch (error) {
      const message = error?.response?.data?.message || 'PC Richard inventory lookup failed'
      setPcLookupByModel((prev) => ({
        ...prev,
        [normalizedModel]: {
          data: null,
          error: message,
          checkedAt: new Date().toISOString(),
        },
      }))
      openSnackbar(message)
    } finally {
      setPcLookupLoadingByModel((prev) => ({ ...prev, [normalizedModel]: false }))
    }
  }

  const renderPcLookupSummary = (modelNumber) => {
    const normalizedModel = String(modelNumber || '').trim().toUpperCase()
    if (!normalizedModel) return null

    if (pcLookupLoadingByModel[normalizedModel]) {
      return <div className={styles.pcInventoryLookup}>Checking PC inventory...</div>
    }

    const lookup = pcLookupByModel[normalizedModel]
    if (!lookup) {
      return (
        <div className={styles.pcInventoryLookup}>
          <div className={styles.pcInventoryHeader}>PC Richard</div>
          <Button
            color="primary"
            size="small"
            type="button"
            variant="outlined"
            style={{ textTransform: 'none' }}
            onClick={() => lookupPcModelInventory(normalizedModel)}
          >
            Check PC inventory
          </Button>
        </div>
      )
    }

    if (lookup.error) {
      return <div className={`${styles.pcInventoryLookup} ${styles.pcInventoryError}`}>{lookup.error}</div>
    }

    const summary = lookup.data?.inventorySummary
    const inventoryStatus = lookup.data?.inventoryStatus || lookup.data?.data?.inventoryStatus || 'Not provided'
    if (summary?.stores) {
      const lookupStatus = lookup.data?.onOrderLookupStatus || 'not checked'
      const lookupChecked = lookupStatus === 'checked'
      return (
        <div className={styles.pcInventoryLookup}>
          <div className={styles.pcInventoryHeader}>
            PC Richard inventory
          </div>
          <div className={styles.pcInventoryGrid}>
            <span><strong>Inventory Status:</strong> {inventoryStatus}</span>
            <span><strong>Available:</strong> {summary.totals?.balance ?? 0}</span>
            <span><strong>FD:</strong> {summary.totals?.farmingdaleQuantity ?? summary.totals?.quantity ?? 0}</span>
            <span><strong>NJ:</strong> {summary.totals?.njQuantity ?? summary.stores?.cartaret?.quantity ?? 0}</span>
            <span><strong>Sched:</strong> {summary.totals?.scheduled ?? 0}</span>
            <span><strong>Asap:</strong> {summary.totals?.asap ?? 0}</span>
            <span><strong>On Order:</strong> {summary.totals?.onOrder ?? 0}</span>
            <span>
              <strong>Lookup:</strong>
              <span className={styles.pcLookupValue}>
                <span className={styles.pcLookupDesktopValue}>{lookupStatus}</span>
                <span
                  className={`${styles.pcLookupMobileValue} ${
                    lookupChecked ? styles.pcLookupSuccess : styles.pcLookupMuted
                  }`}
                  title={lookupStatus}
                  aria-label={lookupStatus}
                >
                  {lookupChecked ? '✓' : 'X'}
                </span>
              </span>
            </span>
          </div>
        </div>
      )
    }

    const rows = getPcInventoryRows(lookup.data?.data)
    if (!rows.length) {
      return <div className={styles.pcInventoryLookup}>No PC inventory details returned.</div>
    }

    const displayPairs = getPcDisplayPairs(rows[0])

    return (
      <div className={styles.pcInventoryLookup}>
        <div className={styles.pcInventoryHeader}>
          PC Richard {rows.length > 1 ? `${rows.length} records` : 'inventory'}
        </div>
        <div className={styles.pcInventoryGrid}>
          <span><strong>Inventory Status:</strong> {inventoryStatus}</span>
          {displayPairs.map(([label, value]) => (
            <span key={`${normalizedModel}-${label}`}>
              <strong>{label}:</strong> {value}
            </span>
          ))}
        </div>
      </div>
    )
  }

  const getSelectedPcModels = () =>
    Array.from(new Set(
      (invoiceData.items || [])
        .map((item) => String(item?.modelNumber || '').trim().toUpperCase())
        .filter(Boolean)
    ))

  const getScheduledOnOrderRows = (selectedModels) =>
    selectedModels
      .flatMap((modelNumber) => pcLookupByModel[modelNumber]?.data?.onOrderRows || [])
      .sort((a, b) => moment(a.arrival, ['M/D/YY', 'M/D/YYYY']).valueOf() - moment(b.arrival, ['M/D/YY', 'M/D/YYYY']).valueOf())

  const renderScheduledOnOrderTable = () => {
    const selectedModels = getSelectedPcModels()
    const rows = getScheduledOnOrderRows(selectedModels)
    const lookups = selectedModels.map((modelNumber) => pcLookupByModel[modelNumber]).filter(Boolean)
    const hasCompletedLookup = lookups.some((lookup) => lookup?.data)
    if (!hasCompletedLookup) return null
    const modelsWithRows = new Set(rows.map((row) => String(row.modelNumber || '').trim().toUpperCase()))
    const checkedModelsWithoutRows = lookups
      .filter((lookup) => lookup?.data?.onOrderLookupStatus === 'checked')
      .map((lookup) => lookup.data?.modelNumber || 'Unknown model')
      .filter((modelNumber) => !modelsWithRows.has(String(modelNumber || '').trim().toUpperCase()))
    const skippedModels = lookups
      .filter((lookup) => lookup?.data?.onOrderLookupStatus === 'skipped-no-on-order')
      .map((lookup) => lookup.data?.modelNumber)
      .filter(Boolean)

    return (
      <div className={styles.pcOnOrderTable}>
        <div className={styles.pcOnOrderTitle}>Scheduled On Order</div>
        {rows.length ? (
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Model</th>
                <th>Location</th>
                <th>Qty</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={`${row.modelNumber}-${row.arrival}-${row.locationNum}-${index}`}>
                  <td>{row.arrival}</td>
                  <td>{row.modelNumber}</td>
                  <td>{row.locationNum}</td>
                  <td>{row.planQty}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        {(checkedModelsWithoutRows.length || skippedModels.length) ? (
          <div className={styles.pcOnOrderEmpty}>
            {checkedModelsWithoutRows.map((modelNumber) => (
              <div key={modelNumber}>
                No scheduled deliveries found for {modelNumber}.
              </div>
            ))}
            {skippedModels.map((modelNumber) => (
              <div key={`skipped-${modelNumber}`}>
                No on-order lookup was needed for {modelNumber}.
              </div>
            ))}
          </div>
        ) : (
          rows.length ? null : <div className={styles.pcOnOrderEmpty}>No scheduled on-order details available.</div>
        )}
      </div>
    )
  }

  const handleProductChange = (index, e, value) => {
    setProduct(value);
    const values = [...invoiceData.items];
    if (value !== null) {
      values[index]['modelNumber'] = value.modelNumber;
      values[index]['unitPrice'] = formatCentsToDollars(value.price);
      values[index]['unitCost'] = formatCentsToDollars(value.cost);
      lookupPcModelInventory(value.modelNumber);
    } else {
      values[index]['modelNumber'] = '';
      values[index]['unitPrice'] = '';
      values[index]['unitCost'] = '';
    }
    setInvoiceData({ ...invoiceData, items: values });
  }

  const handleAccountChange = (value) => {
    setAccount(value);
    setLocationTarget(null);
    setContactTarget(null);
    const billSnapshot = value ? {
      accountId: value._id || value.accountId,
      name: value.name,
      billingAddress: value.billingAddress,
      billingCity: value.billingCity,
      billingState: value.billingState,
      billingZip: value.billingZip,
      taxIdNumber: value.taxIdNumber,
      term: value.term,
      phone: value.phone,
      email: value.email,
    } : null
    setInvoiceData((prev) => ({
      ...prev,
      billTo: billSnapshot,
      client: billSnapshot ? {
        store: billSnapshot.name,
        address: billSnapshot.billingAddress,
        city: billSnapshot.billingCity,
        state: billSnapshot.billingState,
        zipCode: billSnapshot.billingZip,
        taxIdNumber: billSnapshot.taxIdNumber,
        term: billSnapshot.term,
        email: billSnapshot.email,
        cellPhone: billSnapshot.phone,
      } : prev.client,
      shipTo: billSnapshot ? null : prev.shipTo,
      contact: billSnapshot ? null : prev.contact,
    }))
    if (value) {
      let tempDueDate = moment(selectedRbDate).add(value.term || 1, 'days');
      setSelectedDate(tempDueDate); 
    } else {
      let tempDueDate = moment(selectedRbDate).add(1, 'days');
      setSelectedDate(tempDueDate);
    }
    handleContactChange(null)
  }

  const handleLocationChange = (value) => {
    setLocationTarget(value);
    const targetLocationId = getLocationId(value)
    const contactsForLocation = value
      ? (Array.isArray(value.contacts) && value.contacts.length
          ? value.contacts
          : contacts.filter((c) => contactMatchesLocation(c, targetLocationId)))
      : [];
    const shipSnapshot = value ? {
      locationId: value._id || value.locationId,
      accountId: value.accountId,
      name: value.name,
      shippingAddress: value.shippingAddress,
      shippingCity: value.shippingCity,
      shippingState: value.shippingState,
      shippingZip: value.shippingZip,
      phone: value.phone || value.cellPhone,
      email: value.email,
      active: value.active,
    } : null
    const primaryContact = contactsForLocation[0] || null
    setContactTarget(primaryContact);
    setInvoiceData((prev) => ({ ...prev, shipTo: shipSnapshot, contact: primaryContact, contactList: contactsForLocation }))
    if (value) {
      applyTaxRateFromLocation(value)
    } 
  }

  const handleLocationSaved = (loc) => {
    if (!loc) return
    handleLocationChange(loc)
  }

  const handleContactSaved = (contact) => {
    if (!contact) return
    const contactObj = Array.isArray(contact.locationIds) ? contact : { ...contact, locationIds: contact.locationId ? [contact.locationId] : [] }
    setContactTarget((prev) => {
      const targetLocId = getLocationId(locationTarget)
      if (targetLocId && !contactObj.locationIds?.some((id) => String(id) === String(targetLocId))) {
        return prev
      }
      return contactObj
    })
    setInvoiceData((prev) => ({ ...prev, contact: contactObj, contactList: [contactObj] }))
  }

  const handleProductSaved = (product) => {
    const userId = user?.result?._id || user?.result?.googleId
    if (userId) {
      dispatch(getProductsByUser({ search: userId }))
    }
  }

  const handleContactChange = (value) => {
    setContactTarget(value);
    const locationIdForContact = value
      ? (value.locationId || (Array.isArray(value.locationIds) && value.locationIds.length ? value.locationIds[0] : null) || getLocationId(locationTarget))
      : null
    const contactSnapshot = value ? {
      contactId: value._id,
      accountId: value.accountId,
      locationId: locationIdForContact,
      name: value.name,
      role: value.role,
      email: value.email,
      phone: value.phone,
      primary: value.primary,
    } : null
    setInvoiceData((prev) => ({ ...prev, contact: contactSnapshot }))
  }

  // console.log(invoiceData)
  // Change handler for dynamically added input field
  const handleChange = (index, e) => {
    const values = [...invoiceData.items];
    values[index][e.target.name] = e.target.value;
    console.log(values);
    setInvoiceData({ ...invoiceData, items: values });
  };

  const handleQuantityChange = (index, e) => {
    const values = [...invoiceData.items];
    if (values[index]['lockup'] === true || values[index]['doNotOrder'] === true) {
      let arr = values[index]['lockup'] ? lockups : inventories
      let str = values[index]['lockup'] ? 'lockup' : 'inventory'
      let l = arr.find(obj => {
        if (obj.modelNumber === values[index]['modelNumber']) {
          if (isEditingInvoice) {
            let originalQty = (parseInt(obj.quantity)) + parseInt(originalItems.current[index]['quantity'])
            if ( originalQty < values[index]['quantity']) {
              openSnackbar(`There are only ${originalQty} ${obj.modelNumber} in ${str}.`)
              values[index][e.target.name] = originalItems.current[index]['quantity'];
            } else {
              values[index]['unitPrice'] = formatCentsToDollars(obj.price);
              values[index]['unitCost'] = formatCentsToDollars(obj.cost);
              values[index][e.target.name] = e.target.value;
            }
            return true;
          }
          else {
            if (obj.quantity < values[index]['quantity']) {
              openSnackbar(`There are only ${obj.quantity} ${obj.modelNumber} in ${str}.`)
              values[index][e.target.name] = originalItems.current[index]['quantity'];
            } else {
              values[index]['unitPrice'] = formatCentsToDollars(obj.price);
              values[index]['unitCost'] = formatCentsToDollars(obj.cost);
              values[index][e.target.name] = e.target.value;
            }
            return true;
          }
          
        } else {
          return false;
        }
      })
      if (l === undefined) {
          openSnackbar(`${values[index]['modelNumber']} is not in ${str}.`)
          values[index][e.target.name] = originalQuantity;
      }
    }
    setInvoiceData({ ...invoiceData, items: values });
  };


  const handlePriceChange = (index, e) => {
    const values = [...invoiceData.items];
    values[index][e.target.name] = e.target.value;
  }

  useEffect(() => {
    //Get the subtotal
    const subTotal = () => {
      var arr = document.getElementsByName("amount");
      var subtotal = 0;
      for (var i = 0; i < arr.length; i++) {
        if (arr[i].value) {
          subtotal += +arr[i].value;
        }
        // document.getElementById("subtotal").value = subtotal;
        setSubTotal(subtotal);
      }
      const effectiveDeliveryFeeOverride = Boolean(deliveryFeeOverride || invoiceData?.deliveryFeeOverride);
      if (effectiveDeliveryFeeOverride) {
        const explicitDeliveryFee = Number(invoiceData?.deliveryFee);
        if (Number.isFinite(explicitDeliveryFee)) {
          setDeliveryFee(explicitDeliveryFee);
        }
        return;
      }

      setDeliveryFee(0.00)
      if (invoiceData?.deliveryFee !== 0) {
        setInvoiceData((prevState) => prevState.deliveryFeeOverride ? prevState : { ...prevState, deliveryFee: 0 })
      }
      if (deliveryCost !== 0) {
        setDeliveryCost(0)
      }
    };

    subTotal();
  }, [invoiceData, deliveryFeeOverride, deliveryCost]);

  // useEffect(() => {
  //   if (client) {
  //     let tempDueDate = moment(selectedRbDate).add(client.term, 'days');
  //     setSelectedDate(tempDueDate); 
  //   } else {
  //     let tempDueDate = moment(selectedRbDate).add(1, 'days');
  //     setSelectedDate(tempDueDate);
  //   }
  // }, [client])

  const totalItemQuantity = useMemo(() => (
    (invoiceData.items || []).reduce((sum, item) => sum + (Number(item.quantity) || 0), 0)
  ), [invoiceData.items])
  const haulAwayUnits = haulAway ? getLiveServiceQuantity(haulAwayQuantity) : 0;
  const haulAwayOption = haulAway ? { cost: haulAwayCost, price: haulAwayFee } : HAUL_AWAY_OPTIONS[0];
  const haulAwayOptions = HAUL_AWAY_OPTIONS.some((option) => option.cost === haulAwayOption.cost && option.price === haulAwayOption.price)
    ? HAUL_AWAY_OPTIONS
    : [...HAUL_AWAY_OPTIONS, haulAwayOption];
  const activeWalkUpChargeRows = getActiveWalkUpChargeRows(walkUpCharges)
  const haulFee = haulAway ? (Number(haulAwayFee) || 0) * haulAwayUnits : 0;
  const walkFee = activeWalkUpChargeRows.reduce((sum, row) => sum + (Number(row.total) || 0), 0);
  const taxableBase = subTotal + deliveryFee + haulFee + walkFee;

  useEffect(() => {
    const total = () => {
      //VAT is calculated as tax rates /100 * subtotal
      let cents = formatDollarsToCents(taxableBase);
      let totalTaxCents = (rates / 100) * cents;
      let totalTax = parseFloat(formatCentsToDollars(totalTaxCents).toFixed(2));
      const overallSum = totalTax + taxableBase;
      setSalesTax(totalTax);
      setTotal(overallSum);
    };
    total();
  }, [rates, taxableBase]);

  const handleAddField = (e) => {
    e.preventDefault();
    setInvoiceData((prevState) => ({
      ...prevState,
      items: [
        ...prevState.items,
        { modelNumber: "", unitPrice: "", quantity: "", amount: "", unitCost: "", discount: "", doNotOrder: false, lockup: false },
      ],
    }));
  };

  const handleRemoveField = (index) => {
    const values = invoiceData.items;
    values.splice(index, 1);
    if (values.length === 0) {
      setInvoiceData((prevState) => ({
        ...prevState,
        items: [
          { modelNumber: "", unitPrice: "", quantity: "", amount: "", unitCost: "", discount: "", doNotOrder: false, lockup: false },
        ],
      }));
    } else {  
      setInvoiceData((prevState) => ({ ...prevState, values }));
    }
    
  };

  const handleHaulAwayFeeChange = (value) => {
    const fee = Number(value?.price) || 0
    const cost = Number(value?.cost) || 0
    if (fee > 0 && totalItemQuantity <= 0) {
      openSnackbar(HAUL_AWAY_QUANTITY_LIMIT_MESSAGE)
      return
    }
    const currentQuantity = getLiveServiceQuantity(haulAwayQuantity) || 1
    const nextQuantity = fee > 0 ? String(Math.min(currentQuantity, totalItemQuantity)) : '1'
    setHaulAway(fee > 0)
    setHaulAwayFee(fee)
    setHaulAwayCost(cost)
    setHaulAwayQuantity(nextQuantity)
    setInvoiceData((prev) => ({
      ...prev,
      haulAway: fee > 0,
      haulAwayFee: fee,
      haulAwayCost: cost,
      haulAwayQuantity: fee > 0 ? normalizeWalkupHaulawayUnits(nextQuantity) : 1,
    }))
  }

  const handleHaulAwayQuantityChange = (value) => {
    const nextQuantity = getLiveServiceQuantity(value)
    if (nextQuantity > totalItemQuantity) {
      openSnackbar(HAUL_AWAY_QUANTITY_LIMIT_MESSAGE)
      return
    }
    setHaulAwayQuantity(value)
    setInvoiceData((prev) => ({ ...prev, haulAwayQuantity: value }))
  }

  const handleHaulAwayQuantityBlur = () => {
    if (!haulAway) return
    const currentQuantity = getLiveServiceQuantity(haulAwayQuantity)
    if (currentQuantity <= 0) {
      const nextQuantity = totalItemQuantity > 0 ? '1' : ''
      setHaulAwayQuantity(nextQuantity)
      setInvoiceData((prev) => ({ ...prev, haulAwayQuantity: nextQuantity || 1 }))
    }
  }

  const syncWalkUpChargeRows = (nextRows) => {
    const rows = nextRows.length ? nextRows : [createWalkUpChargeRow()]
    const legacyFields = getWalkUpLegacyFields(rows)
    setWalkUpCharges(rows)
    setInvoiceData((prev) => ({
      ...prev,
      ...legacyFields,
      walkupHaulawayUnits: Math.max(
        getSavedServiceQuantity(prev.haulAwayQuantity, prev.walkupHaulawayUnits),
        legacyFields.walkUpQuantity,
        1
      ),
    }))
  }

  const handleWalkUpChargeChange = (index, changes) => {
    const nextRows = walkUpCharges.map((row, rowIndex) => (
      rowIndex === index ? { ...row, ...changes } : row
    ))
    if (getWalkUpQuantityTotal(nextRows) > totalItemQuantity) {
      openSnackbar(WALK_UP_QUANTITY_LIMIT_MESSAGE)
      return
    }
    syncWalkUpChargeRows(nextRows)
  }

  const handleAddWalkUpCharge = () => {
    if (getWalkUpQuantityTotal(walkUpCharges) >= totalItemQuantity) {
      openSnackbar(WALK_UP_QUANTITY_LIMIT_MESSAGE)
      return
    }
    syncWalkUpChargeRows([...walkUpCharges, createWalkUpChargeRow()])
  }

  const handleRemoveWalkUpCharge = (index) => {
    const nextRows = walkUpCharges.filter((_, rowIndex) => rowIndex !== index)
    syncWalkUpChargeRows(nextRows)
  }

  useEffect(() => {
    let capped = false
    const currentHaulAwayQuantity = getLiveServiceQuantity(haulAwayQuantity)
    if (haulAway && currentHaulAwayQuantity > totalItemQuantity) {
      const nextQuantity = totalItemQuantity > 0 ? String(totalItemQuantity) : ''
      setHaulAwayQuantity(nextQuantity)
      setInvoiceData((prev) => ({ ...prev, haulAwayQuantity: nextQuantity || 1 }))
      capped = true
    }

    if (getWalkUpQuantityTotal(walkUpCharges) > totalItemQuantity) {
      syncWalkUpChargeRows(capWalkUpChargeRowsToQuantity(walkUpCharges, totalItemQuantity))
      capped = true
    }

    if (capped) {
      openSnackbar('Service quantities cannot be more than total items.')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalItemQuantity])

  const saveInvoice = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    const selectedContacts = (locationContactsForTarget && locationContactsForTarget.length)
      ? locationContactsForTarget
      : normalizeInvoiceContactList({ contact: invoiceData.contact, contactList: invoiceData.contactList })
    const primaryContact = selectedContacts[0] || null
    const submitHaulAwayQuantity = haulAway ? normalizeWalkupHaulawayUnits(haulAwayQuantity) : 1
    const submitWalkFields = getWalkUpLegacyFields(walkUpCharges)
    const submitWalkUpQuantity = submitWalkFields.walkUp ? submitWalkFields.walkUpQuantity : 1
    if (haulAway && submitHaulAwayQuantity > totalItemQuantity) {
      openSnackbar(HAUL_AWAY_QUANTITY_LIMIT_MESSAGE)
      setIsSubmitting(false)
      return
    }
    if (submitWalkFields.walkUp && submitWalkUpQuantity > totalItemQuantity) {
      openSnackbar(WALK_UP_QUANTITY_LIMIT_MESSAGE)
      setIsSubmitting(false)
      return
    }
    const submitLegacyServiceUnits = Math.max(submitHaulAwayQuantity, submitWalkUpQuantity, 1)
    const submitHaulFee = haulAway ? (Number(haulAwayFee) || 0) * submitHaulAwayQuantity : 0
    const submitWalkFee = submitWalkFields.walkUp ? submitWalkFields.walkUpFee : 0
    const submitTaxableBase = subTotal + deliveryFee + submitHaulFee + submitWalkFee
    const submitSalesTaxCents = (rates / 100) * formatDollarsToCents(submitTaxableBase)
    const submitSalesTax = parseFloat(formatCentsToDollars(submitSalesTaxCents).toFixed(2))
    const submitTotal = submitTaxableBase + submitSalesTax
    const payload = {
      ...invoiceData,
      billTo: invoiceData.billTo || null,
      shipTo: invoiceData.shipTo || null,
      contact: primaryContact,
      contactList: selectedContacts,
      haulAway,
      haulAwayFee: haulAway ? Number(haulAwayFee) || 0 : 0,
      haulAwayCost: haulAway ? Number(haulAwayCost) || 0 : 0,
      walkUp: submitWalkFields.walkUp,
      walkUpCharges: submitWalkFields.walkUpCharges,
      haulAwayQuantity: submitHaulAwayQuantity,
      walkUpQuantity: submitWalkUpQuantity,
      walkupHaulawayUnits: submitLegacyServiceUnits,
      walkUpFee: submitWalkFee,
      client: invoiceData.billTo ? {
        store: invoiceData.billTo.name,
        address: invoiceData.billTo.billingAddress,
        city: invoiceData.billTo.billingCity,
        state: invoiceData.billTo.billingState,
        zipCode: invoiceData.billTo.billingZip,
        taxIdNumber: invoiceData.billTo.taxIdNumber,
        term: invoiceData.billTo.term,
        email: invoiceData.billTo.email,
        cellPhone: invoiceData.billTo.phone,
      } : invoiceData.client,
      subTotal: submitTaxableBase,
      total: submitTotal,
      salesTax: submitSalesTax,
      rates: rates,
      dueDate: selectedDate,
      receivedByDate: selectedRbDate,
      deliveryFee: deliveryFee,
      deliveryCost: deliveryCost,
      deliveryFeeOverride: deliveryFeeOverride,
      type: type,
      status: status,
      paymentRecords: invoiceData.paymentRecords || [],
    }
    if (isEditingInvoice && activeInvoice) {
      try {
        await dispatch(
          updateInvoice(activeInvoice._id, {
            ...payload
          })
        );
      } catch (error) {
        openSnackbar(error?.response?.data?.message || 'Unable to save invoice. Please try again.');
        setIsSubmitting(false);
        return;
      }
      invoiceData.items.forEach((item, index) => {
        const originalItem = originalItems.current[index] || {
          lockup: false,
          doNotOrder: false,
          quantity: 0,
          modelNumber: "",
        };
        let l = lockups.find(obj => {
          if (item.lockup || originalItem.lockup) {
            if ((item.modelNumber === obj.modelNumber) || (originalItem.lockup === obj.modelNumber)) {
              return true;
            }
          }
        });
        if (l !== undefined) {
          if (originalItem.lockup === false) {
            l.quantity -= item.quantity;
          } else {
            if (originalItem.quantity < item.quantity) {
              l.quantity -= (item.quantity - originalItem.quantity)
            } else if (originalItem.quantity > item.quantity) {
              l.quantity += (originalItem.quantity - item.quantity)
            } else {
              if (item.lockup === false)
              {
                l.quantity += parseInt(item.quantity);
              } 
            }
          }
          dispatch(updateLockup(l._id, l, openSnackbar))
        }
        let inv = inventories.find(obj => {
          if (item.doNotOrder || originalItem.doNotOrder) {
            if ((item.modelNumber === obj.modelNumber) || (originalItem.doNotOrder === obj.modelNumber)) {
              return true;
            }
          }
        });
        if (inv !== undefined) {
          if (originalItem.doNotOrder === false) {
            inv.quantity -= item.quantity;
          } else {
            if (originalItem.quantity < item.quantity) {
              inv.quantity -= (item.quantity - originalItem.quantity)
            } else if (originalItem.quantity > item.quantity) {
              inv.quantity += (originalItem.quantity - item.quantity)
            } else {
              if (item.doNotOrder === false) {
                inv.quantity += item.quantity;
              }
              
            }
          }
          dispatch(updateInventory(inv._id, inv, openSnackbar))
        }
      })
      history.push(`/invoice/${activeInvoice._id}`);
    } else {
      let createdInvoice = null;
      try {
        createdInvoice = await dispatch(
          createInvoice(
            {
              ...payload,
              createRequestId: createRequestIdRef.current,
              invoiceNumber: `${
                invoiceData.invoiceNumber < 100
                  ? Number(invoiceData.invoiceNumber).toString().padStart(3, "0")
                  : Number(invoiceData.invoiceNumber)
              }`,
              creator: [user?.result?._id || user?.result?.googleId],
            },
            history
          )
        );
      } catch (error) {
        openSnackbar(error?.response?.data?.message || 'Unable to save invoice. Please try again.');
        setIsSubmitting(false);
        return;
      }
      if (quoteDraftQuoteIdRef.current && createdInvoice?._id) {
        try {
          await updateQuote(quoteDraftQuoteIdRef.current, {
            status: 'Converted',
            convertedInvoiceId: createdInvoice._id,
            convertedInvoiceNumber: createdInvoice.invoiceNumber,
            convertedAt: new Date().toISOString(),
          });
        } catch (error) {
          console.log(error);
        }
      }
      invoiceData.items.forEach(item => {
        let l = lockups.find(obj => {
          if (item.lockup) {
            if (item.modelNumber === obj.modelNumber) {
              return true;
            }
          }
        });
        if (l !== undefined) {
          l.quantity -= item.quantity;
          dispatch(updateLockup(l._id, l, openSnackbar))
        }
        let inv = inventories.find(obj => {
          if (item.doNotOrder) {
            if (item.modelNumber === obj.modelNumber) {
              return true;
            }
          }
        });
        if (inv !== undefined) {
          inv.quantity -= item.quantity;
          dispatch(updateInventory(inv._id, inv, openSnackbar))
        }
      })
    }

    // setInvoiceData(initialState)
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting || isCheckingMargins) return;

    const itemsToCheck = (invoiceData.items || [])
      .map((item, itemIndex) => ({ ...item, itemIndex }))
      .filter((item) => (
        String(item?.modelNumber || '').trim() && Number(item?.quantity) > 0
      ));
    if (!itemsToCheck.length) {
      await saveInvoice();
      return;
    }

    setIsCheckingMargins(true);
    try {
      const { data } = await fetchMarginComparison(itemsToCheck);
      const recommendedTierId = getRecommendedDeliveryTierId(data);
      const estimate = calculateMarginComparison(data, recommendedTierId);

      if (estimate.hasWholesaleSavings) {
        setMarginComparison(data);
        setMarginDialogOpen(true);
      } else {
        await saveInvoice();
      }
    } catch (error) {
      openSnackbar(error?.response?.data?.message || 'Unable to compare PM and Wholesale pricing. The invoice was not saved.');
    } finally {
      setIsCheckingMargins(false);
    }
  };

  const handleApplyWholesaleCosts = (estimate) => {
    const selectedRows = (estimate?.rows || []).filter((row) => row.selectedForWholesale);
    const selectedByItemIndex = new Map(
      selectedRows.map((row) => [String(row.itemIndex), row])
    );
    const nextDeliveryCost = Number(((Number(estimate?.wholesaleDeliveryCents) || 0) / 100).toFixed(2));

    setInvoiceData((prev) => ({
      ...prev,
      items: (prev.items || []).map((item, itemIndex) => {
        const selectedRow = selectedByItemIndex.get(String(itemIndex));
        if (!selectedRow || selectedRow.wholesaleUnitCostCents === null) return item;
        return {
          ...item,
          unitCost: Number((selectedRow.wholesaleUnitCostCents / 100).toFixed(2)),
        };
      }),
      deliveryCost: nextDeliveryCost,
      deliveryFeeOverride: true,
    }));
    setDeliveryCost(nextDeliveryCost);
    setDeliveryFeeOverride(true);
    setMarginDialogOpen(false);
    setMarginComparison(null);
    openSnackbar(`Applied ${selectedRows.length} Wholesale cost${selectedRows.length === 1 ? '' : 's'} and a $${nextDeliveryCost.toFixed(2)} delivery cost to this invoice.`);
  };

  const classes = useStyles();
  const isMobile = useMediaQuery("(max-width:768px)");
  const CustomPaper = (props) => {
    return <Paper elevation={3} {...props} />;
  };

  if (!user) {
    history.push("/login");
  }

  return (
    <>
    <div className={styles.invoiceLayout}>
      <form onSubmit={handleSubmit} className="mu-form">
        <Container className={classes.headerContainer}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item xs={12} sm={8} className={styles.headerNumber}>
              <Typography variant="overline" style={{ color: "gray" }}>
                Invoice #
              </Typography>
              <Typography variant="h6" style={{ fontWeight: 700 }}>
                {invoiceData.invoiceNumber || nextInvoiceNumber || 'Pending'}
              </Typography>
              <Grid container spacing={1} style={{ marginTop: 4, marginBottom: 8 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="PC Invoice #"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={invoiceData.pcInvoiceNumber || ''}
                    onChange={(e) => setInvoiceData((prev) => ({ ...prev, pcInvoiceNumber: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Client PO #"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={invoiceData.clientPONumber || ''}
                    onChange={(e) => setInvoiceData((prev) => ({ ...prev, clientPONumber: e.target.value }))}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} sm="auto" className={styles.headerType}>
              <InvoiceType type={type} setType={setType} />
            </Grid>
          </Grid>
        </Container>
        <Divider />
        <Container>
          <Grid
            container
            spacing={3}
            alignItems="flex-start"
            justifyContent="space-between"
            wrap={isMobile ? "wrap" : "nowrap"}
            style={{ marginTop: "40px" }}
          >
            <Grid item xs={12} md={8}>
              <Container>
                <Typography
                  variant="overline"
                  style={{ color: "gray", paddingRight: "3px" }}
                  gutterBottom
                >
                  Bill to
                </Typography>

                {account && (
                  <>
                    <Typography variant="subtitle2" gutterBottom>
                      {account.name}
                    </Typography>
                    <Typography variant="body2">{account.email}</Typography>
                    <Typography variant="body2">{account.phone}</Typography>
                    <Typography variant="body2">{account.billingAddress}</Typography>
                    <Typography variant="body2">{[account.billingCity, account.billingState, account.billingZip].filter(Boolean).join(', ')}</Typography>
                    <Button
                      color="primary"
                      size="small"
                      style={{ textTransform: "none" }}
                      onClick={() => handleAccountChange(null)}
                    >
                      Change
                    </Button>
                  </>
                )}
                <div
                  style={account ? { display: "none" } : { display: "block" }}
                >
                  <Autocomplete
                    options={accountOptions}
                    getOptionLabel={(option) => option.name || ''}
                    PaperComponent={CustomPaper}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required={!isEditingInvoice && true}
                        label="Select Account"
                        margin="dense"
                        variant="outlined"
                        size="small"
                      />
                    )}
                    value={account}
                    onChange={(event, value) => handleAccountChange(value)}
                    style={{ maxWidth: 300 }}
                  />
                </div>
                <Grid container spacing={2} style={{ marginTop: 12 }}>
                  <Grid item xs={12} sm={6} style={{ width: '100%' }}>
                    <Typography variant="overline" style={{ color: "gray" }} gutterBottom>
                      Ship To
                    </Typography>
                    {locationTarget ? (
                      <>
                        <Typography variant="subtitle2" gutterBottom>
                          {locationTarget.name}
                        </Typography>
                      <Typography variant="body2">{locationTarget.shippingAddress}</Typography>
                      <Typography variant="body2">
                        {[locationTarget.shippingCity, locationTarget.shippingState, locationTarget.shippingZip].filter(Boolean).join(', ')}{locationTarget.phone ? ` ${locationTarget.phone}` : ''}
                      </Typography>
                        <Button
                          color="primary"
                          size="small"
                          style={{ textTransform: "none", marginTop: 6 }}
                          onClick={() => { setLocationTarget(null); setInvoiceData((prev) => ({ ...prev, shipTo: null })); }}
                        >
                          Change
                        </Button>
                        <Button
                          color="primary"
                          size="small"
                          style={{ textTransform: "none", marginTop: 6, marginLeft: 8 }}
                          onClick={() => setLocationModalOpen(true)}
                        >
                          Add Location
                        </Button>
                      </>
                    ) : (
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Autocomplete
                          options={locationOptions}
                          getOptionLabel={(option) => option.name || ''}
                          PaperComponent={CustomPaper}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Select Location"
                              margin="dense"
                              variant="outlined"
                              size="small"
                              fullWidth
                            />
                          )}
                          value={locationTarget}
                          onChange={(event, value) => handleLocationChange(value)}
                          disabled={!account && accounts.length > 0}
                          style={{ width: '100%', maxWidth: 480 }}
                        />
                        <Button
                          color="primary"
                          size="small"
                          variant="outlined"
                          disabled={!account}
                          onClick={() => setLocationModalOpen(true)}
                          style={{ textTransform: 'none', marginBottom: 5 }}
                        >
                          Add Location
                        </Button>
                      </div>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6} style={{ width: '100%' }}>
                    <Typography variant="overline" style={{ color: "gray" }} gutterBottom>
                      Contact
                    </Typography>
                    {locationContactsForTarget.length ? (
                      locationContactsForTarget.map((c) => (
                        <div key={c._id || c.name} style={{ marginBottom: 4 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            {c.name} {c.primary ? '• Primary' : ''}
                          </Typography>
                          <Typography variant="body2">{[c.phone].filter(Boolean).join(' • ')}</Typography>
                        </div>
                      ))
                    ) : (
                      <Typography variant="body2" color="textSecondary">No contacts for this location.</Typography>
                    )}
                    <Button
                      color="primary"
                      size="small"
                      variant="outlined"
                      disabled={!account}
                      onClick={() => setContactModalOpen(true)}
                      style={{ textTransform: 'none', marginTop: 8, marginBottom: 5 }}
                    >
                      Add Contact
                    </Button>
                  </Grid>
                </Grid>
              </Container>
            </Grid>

            <Grid item xs={12} md={4} style={{ textAlign: "right", minWidth: 240 }}>
              <Typography
                variant="overline"
                style={{ color: "gray" }}
                gutterBottom
              >
                Status
              </Typography>
              <Typography
                variant="h6"
                gutterBottom
                style={{ color: type === "Receipt" ? "green" : "red" }}
              >
                {type === "Receipt" ? "Paid" : "Unpaid"}
              </Typography>
              <Typography
                variant="overline"
                style={{ color: "gray" }}
                gutterBottom
              >
                Date
              </Typography>
              <Typography variant="body2" gutterBottom>
              {selectedRbDate
                  ? moment(selectedRbDate).format("MMM Do YYYY")
                  : "27th Sep 2021"}
              </Typography>
              <Typography
                variant="overline"
                style={{ color: "gray" }}
                gutterBottom
              >
                Due Date
              </Typography>
              <Typography variant="body2" gutterBottom>
                {selectedDate
                  ? moment(selectedDate).format("MMM Do YYYY")
                  : "27th Sep 2021"}
              </Typography>
              <Typography variant="overline" gutterBottom>
                Amount
              </Typography>
              <Typography variant="h6" gutterBottom>
                ${toCommas(total.toFixed(2))}
              </Typography>
            </Grid>
          </Grid>
        </Container>

        <div className={styles.tableWrapper}>
          <TableContainer component={Paper} className={`tb-container ${styles.tableContainer}`}>
            <Table className={classes.table} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell>Qty</TableCell>
                    {/* <TableCell>#</TableCell> */}
                    <TableCell>Cost ($)</TableCell>
                    <TableCell>Price ($)</TableCell>
                    {/* <TableCell>Disc (%)</TableCell> */}
                    <TableCell>Amount</TableCell>
                    {/* <TableCell>Inv.</TableCell> */}
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
              <TableBody>
                {invoiceData.items.map((itemField, index) => (
                  <TableRow key={index}>
                    <TableCell scope="row" style={{ width: "40%" }}>
                      {" "}
                      {itemField.modelNumber && (
	                        <>
	                            <Typography variant="subtitle1" gutterBottom>
	                                {itemField.modelNumber}
	                            </Typography>
                              {renderPcLookupSummary(itemField.modelNumber)}
	                        </>
	                        )}
                        <div style={itemField.modelNumber ? { display: "none" } : { display: "block" }}>
                        <Autocomplete
                            options={products.sort((a, b) => {
                            if (
                                a.hasOwnProperty("brand") &&
                                b.hasOwnProperty("brand")
                            ) {
                                return -b.brand.localeCompare(a.brand);
                            }
                            })}
                            groupBy={(option) => option.brand}
                            getOptionLabel={(option) => option.modelNumber || ""}
                            PaperComponent={CustomPaper}
                            renderInput={(params) => (
                            <TextField
                                {...params}
                                name="product"
                                label="Select Product"
                                margin="normal"
                                variant="outlined"
                            />
                            )}
                            value={itemField.modelNumber}
                            onChange={(e, value) => handleProductChange(index, e, value)}
                        />
                        </div>
                      {" "}
                    </TableCell>
                    <TableCell align="right">
                      {" "}
                      <InputBase
                        sx={{ ml: 1, flex: 1 }}
                        type="number"
                        name="quantity"
                        onChange={(e) => handleChange(index, e)}
                        onBlur={(e) => handleQuantityChange(index, e)}
                        value={itemField.quantity}
                        placeholder="0"
                      />{" "}
                    </TableCell>
                      {/* <TableCell align="right">
                        <FormGroup>
                          <FormControlLabel control={
                            <Checkbox
                              name="lockup"
                              checked={itemField.lockup}
                                color="primary"
                                onChange={(e) => handleLockupClick(index, e)}
                            />} label="" />
                        </FormGroup>
                      </TableCell> */}
                    <TableCell align="right">
                      {" "}
                      <InputBase
                        sx={{ ml: 1, flex: 1 }}
                        type="number"
                        name="unitCost"
                        onChange={(e) => handleChange(index, e)}
                        value={itemField.unitCost}
                        placeholder="0"
                      />{" "}
                    </TableCell>
                    <TableCell align="right">
                      {" "}
                      <InputBase
                        sx={{ ml: 1, flex: 1 }}
                        type="number"
                        name="unitPrice"
                        onChange={(e) => handleChange(index, e)}
                        value={itemField.unitPrice}
                        placeholder="0"
                      />{" "}
                    </TableCell>
                    {/* <TableCell align="right">
                      {" "}
                      <InputBase
                        sx={{ ml: 1, flex: 1 }}
                        type="number"
                        name="discount"
                        onChange={(e) => handleChange(index, e)}
                        value={itemField.discount}
                        placeholder="0"
                      />{" "}
                    </TableCell> */}
                    <TableCell align="right">
                      {" "}
                      <InputBase
                        sx={{ ml: 1, flex: 1 }}
                        type="number"
                        name="amount"
                        onChange={(e) => handleChange(index, e)}
                        value={
                          (itemField.quantity * itemField.unitPrice -
                          (itemField.quantity *
                            itemField.unitPrice *
                            itemField.discount) /
                            100).toFixed(2)
                        }
                        disabled
                      />{" "}
                    </TableCell>
                    {/* <TableCell align="right">
                      <FormGroup>
                        <FormControlLabel control={
                          <Checkbox
                            name="doNotOrder"
                            checked={itemField.doNotOrder}
                              color="primary"
                              onChange={(e) => handleDoNotOrderClick(index, e)}
                          />} label="" />
                      </FormGroup>
                    </TableCell> */}
                    <TableCell align="right">
                      <IconButton onClick={() => handleRemoveField(index)}>
                        <DeleteOutlineRoundedIcon
                          style={{ width: "20px", height: "20px" }}
                        />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
        </TableContainer>
        <div className={styles.addButton}>
          <button className={styles.plusButton} onClick={handleAddField}>+</button>
          <Button
            color="primary"
            size="small"
            variant="outlined"
            style={{ marginLeft: 8, textTransform: 'none' }}
            onClick={() => setProductModalOpen(true)}
          >
            Add Product
          </Button>
        </div>
        </div>

        <div className={styles.summaryRow}>
          {renderScheduledOnOrderTable()}
          <div className={styles.invoiceSummary}>
            <div className={styles.summary}>Invoice Summary</div>
            <div className={styles.summaryItem}>
              <p>Delivery Fee:</p>
              <h4>${(deliveryFee).toFixed(2)}</h4>
            </div>
            {haulAway && (
              <div className={styles.summaryItem}>
                <p>Haul Away (${Number(haulAwayFee || 0).toFixed(2)} x {haulAwayUnits}):</p>
                <h4>${haulFee.toFixed(2)}</h4>
              </div>
            )}
            {activeWalkUpChargeRows.map((row, index) => (
              <div className={styles.summaryItem} key={`${row.walkUp}-${index}`}>
                <p>Walk Up ({row.walkUp} x {row.quantity}):</p>
                <h4>${Number(row.total || 0).toFixed(2)}</h4>
              </div>
            ))}
            <div className={styles.summaryItem}>
              <p>Sub total:</p>
              <h4>${(taxableBase).toFixed(2)}</h4>
            </div>
            <div className={styles.summaryItem}>
              <p>Sales Tax:</p>
              <h4>${(salesTax).toFixed(2)}</h4>
            </div>
            <div className={styles.summaryItem}>
              <p>Total</p>
              <h4 style={{ color: "black", fontSize: "18px", lineHeight: "8px" }}>
                 ${toCommas(total.toFixed(2))}
              </h4>
            </div>
          </div>
        </div>

        <div className={styles.toolBar}>
          <Container>
            <Grid container>
              <Grid item style={{ marginTop: "16px", marginRight: 10 }}>
                <TextField
                  type="text"
                  step="any"
                  name="rates"
                  id="rates"
                  value={rates}
                  onChange={handleRates}
                  placeholder="e.g 10"
                  label="Tax Rates(%)"
                />
              </Grid>
              <Grid item style={{ marginTop: "16px", marginRight: 10 }}>
                <TextField
                  type="text"
                  step="any"
                  name="delivery"
                  id="delivery"
                  value={deliveryFee}
                  onChange={handleDeliveryFeeChange}
                  label="Delivery Fee ($)"
                />
                <TextField
                  type="text"
                  step="any"
                  name="deliveryCost"
                  id="deliveryCost"
                  value={deliveryCost}
                  onChange={handleDeliveryCostChange}
                  label="Delivery Cost ($)"
                  disabled={!deliveryFeeOverride}
                  style={{ display: "block", marginTop: 8 }}
                />
              </Grid>
              <Grid item style={{ marginTop: "30px", marginRight: 10 }}>
                <FormGroup>
                    <FormControlLabel control={<Checkbox
                              color="primary"
                              checked={deliveryFeeOverride}
                              onChange={handleDeliveryFeeOverride}
                          />} label="Fee Override" />
                  </FormGroup>
              </Grid>
              <Grid item style={{ marginTop: "16px", marginRight: 10, minWidth: 170 }}>
                <Autocomplete
                  options={haulAwayOptions}
                  getOptionLabel={formatHaulAwayOption}
                  getOptionSelected={(option, value) => option.cost === value.cost && option.price === value.price}
                  value={haulAwayOption}
                  PaperComponent={CustomPaper}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Haul Away"
                      helperText="Cost / price per unit"
                      margin="dense"
                      variant="outlined"
                      size="small"
                    />
                  )}
                  onChange={(event, value) => {
                    handleHaulAwayFeeChange(value)
                  }}
                />
              </Grid>
              <Grid item style={{ marginTop: "16px", marginRight: 10, width: 110 }}>
                <TextField
                  type="number"
                  name="haulAwayQuantity"
                  id="haulAwayQuantity"
                  value={haulAwayQuantity}
                  onChange={(e) => {
                    handleHaulAwayQuantityChange(e.target.value)
                  }}
                  onBlur={handleHaulAwayQuantityBlur}
                  label="Haul Qty"
                  margin="dense"
                  variant="outlined"
                  size="small"
                  disabled={!haulAway}
                  inputProps={{ min: 1, step: 1 }}
                />
              </Grid>
              {walkUpCharges.map((charge, index) => {
                const walkUpOptions = [
                  '',
                  ...Object.keys(WALK_UP_FEES),
                  ...(charge.walkUp && !isStandardWalkUp(charge.walkUp) ? [charge.walkUp] : []),
                ]
                return (
                  <React.Fragment key={`walk-up-charge-${index}`}>
                    <Grid item style={{ marginTop: "16px", marginRight: 10, minWidth: 180 }}>
                      <Autocomplete
                        options={walkUpOptions}
                        getOptionLabel={(opt) => {
                          if (!opt) return 'None'
                          return isStandardWalkUp(opt) ? `${opt} (+$${WALK_UP_FEES[opt]})` : opt
                        }}
                        value={charge.walkUp || ''}
                        PaperComponent={CustomPaper}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={index === 0 ? 'Walk Up' : `Walk Up ${index + 1}`}
                            margin="dense"
                            variant="outlined"
                            size="small"
                          />
                        )}
                        onChange={(event, value) => {
                          const val = value || ''
                          handleWalkUpChargeChange(index, {
                            walkUp: val,
                            quantity: charge.quantity === '' ? '1' : charge.quantity,
                            total: isStandardWalkUp(val) ? undefined : charge.total,
                          })
                        }}
                      />
                    </Grid>
                    <Grid item style={{ marginTop: "16px", marginRight: 10, width: 110 }}>
                      <TextField
                        type="number"
                        name={`walkUpQuantity-${index}`}
                        id={`walkUpQuantity-${index}`}
                        value={charge.quantity}
                        onChange={(e) => handleWalkUpChargeChange(index, { quantity: e.target.value })}
                        onBlur={() => {
                          if (charge.walkUp && getLiveServiceQuantity(charge.quantity) <= 0) {
                            handleWalkUpChargeChange(index, { quantity: '1' })
                          }
                        }}
                        label="Walk Qty"
                        margin="dense"
                        variant="outlined"
                        size="small"
                        disabled={!charge.walkUp}
                        inputProps={{ min: 1, step: 1 }}
                      />
                    </Grid>
                    <Grid item style={{ marginTop: "22px", marginRight: 10 }}>
                      {index === walkUpCharges.length - 1 && (
                        <Button
                          type="button"
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={handleAddWalkUpCharge}
                        >
                          +
                        </Button>
                      )}
                      {walkUpCharges.length > 1 && (
                        <IconButton
                          size="small"
                          aria-label="Remove walk up charge"
                          onClick={() => handleRemoveWalkUpCharge(index)}
                        >
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Grid>
                  </React.Fragment>
                )
              })}
              <Grid item style={{ marginRight: 10 }}>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <KeyboardDatePicker
                    margin="normal"
                    id="date-picker-dialog"
                    label="Received By Date"
                    format="MM/dd/yyyy"
                    value={selectedRbDate}
                    onChange={handleRbDateChange}
                    KeyboardButtonProps={{
                      "aria-label": "change date",
                    }}
                  />
                </MuiPickersUtilsProvider>
              </Grid>
              <Grid item style={{ marginRight: 10 }}>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <KeyboardDatePicker
                    margin="normal"
                    id="date-picker-dialog"
                    label="Due date"
                    format="MM/dd/yyyy"
                    value={selectedDate}
                    onChange={handleDateChange}
                    KeyboardButtonProps={{
                      "aria-label": "change date",
                    }}
                  />
                </MuiPickersUtilsProvider>
              </Grid>
            </Grid>
          </Container>
        </div>
        <div className={styles.note}>
          <h4>Note/Payment Info</h4>
          <textarea
            style={{ border: "solid 1px #d6d6d6", padding: "10px" }}
            placeholder="Provide additional details or terms of service"
            onChange={(e) =>
              setInvoiceData({ ...invoiceData, notes: e.target.value })
            }
            value={invoiceData.notes}
          />
        </div>

        {/* <button className={styles.submitButton} type="submit">Save and continue</button> */}
        <div className={styles.actionsBar}>
          <Grid container justifyContent="center">
            <Button
              id="save-invoice"
              variant="contained"
              style={{ justifyContentContent: "center" }}
              type="submit"
              disabled={isSubmitting || isCheckingMargins}
              color="primary"
              size="large"
              className={classes.button}
              startIcon={<SaveIcon />}
            >
              {isSubmitting ? "Saving..." : (isCheckingMargins ? "Checking margins..." : "Save and Continue")}
            </Button>
          </Grid>
        </div>
      </form>
    </div>
    {marginComparison ? (
      <MarginComparisonDialog
        open={marginDialogOpen}
        comparison={marginComparison}
        saving={isSubmitting}
        onSaveWithoutChanges={saveInvoice}
        onCancel={() => setMarginDialogOpen(false)}
        onApply={handleApplyWholesaleCosts}
      />
    ) : null}
    <AddLocation
      open={locationModalOpen}
      setOpen={setLocationModalOpen}
      currentId={null}
      setCurrentId={() => {}}
      defaultAccountId={selectedAccountId}
      onSaved={handleLocationSaved}
    />
    <AddContact
      open={contactModalOpen}
      setOpen={setContactModalOpen}
      currentId={null}
      setCurrentId={() => {}}
      defaultAccountId={selectedAccountId}
      defaultLocationId={selectedLocationId}
      onSaved={handleContactSaved}
    />
    <AddProduct
      open={productModalOpen}
      setOpen={setProductModalOpen}
      currentId={null}
      setCurrentId={() => {}}
      onSaved={handleProductSaved}
    />
    </>
  );
};

export default Invoice;
